<template>
  <div>
    <VBusHelper
      :reactOn="constants.EVENTS.WALLET.ACCOUNT_MODAL_OPEN"
      :handler="showModal"
    />

    <!-- Bottom Sheet Dialog for Entering Account Info -->
    <DialogComponent
      ref="modal"
      dialogId="add-withdrawal-account-modal"
      type="bottomsheet"
      noInnerPadding
      @closed="resetForm"
    >
      <div class="relative w-full p-6 bg-white rounded-t-3xl">
        <!-- Close Button -->
        <button
          type="button"
          @click="hideModal"
          class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-full transition-colors z-10"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="text-2xl font-bold text-slate-800 mb-1 pr-8">
          Nouveau compte de retrait
        </div>
        <p class="text-xs text-slate-500 mb-4">
          Ajoutez un compte Mobile Money pour recevoir vos fonds
        </p>

        <!-- Informative Alert -->
        <div
          v-if="isFedapayPilotProvider"
          class="p-3.5 mb-5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-2.5"
        >
          <span class="text-amber-600 text-base">ℹ️</span>
          <div class="text-xs leading-relaxed">
            <span class="font-bold">Vérification préalable :</span> Si votre compte n'est pas encore enregistré, une demande de confirmation de <strong class="text-amber-700">1 FCFA</strong> vous sera envoyée afin de vérifier que le numéro vous appartient bien.
          </div>
        </div>

        <!-- Form Fields -->
        <div class="flex flex-col gap-4">
          <!-- Account Owner Name -->
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-slate-600 mb-2">
              Nom du titulaire
            </label>
            <input
              v-model="ownerName"
              type="text"
              placeholder="Ex: Jean Dupont"
              class="w-full p-3.5 rounded-xl border border-slate-200 bg-slate-50 focus:border-rose-500 focus:bg-white focus:ring-2 focus:ring-rose-500/10 outline-none transition-all font-medium text-sm"
              required
            />
          </div>

          <!-- Phone Number -->
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-slate-600 mb-2">
              Numéro Mobile Money
            </label>
            <div class="relative">
              <input
                v-model="phoneNumber"
                type="tel"
                placeholder="Ex: 0151010580 ou 96000000"
                class="w-full p-3.5 rounded-xl border border-slate-200 bg-slate-50 focus:border-rose-500 focus:bg-white focus:ring-2 focus:ring-rose-500/10 outline-none transition-all font-mono font-medium text-sm"
                required
              />
            </div>
          </div>

          <!-- Operator -->
          <div>
            <label class="block text-xs uppercase tracking-wider font-bold text-slate-600 mb-2">
              Opérateur réseau
            </label>
            <select
              v-model="operator"
              class="w-full p-3.5 rounded-xl border border-slate-200 bg-slate-50 focus:border-rose-500 focus:bg-white focus:ring-2 focus:ring-rose-500/10 outline-none transition-all font-medium text-sm"
            >
              <option value="MTN">MTN Mobile Money</option>
              <option value="MOOV">MOOV Money</option>
              <option value="CELTIIS">CELTIIS Cash</option>
              <option value="ORANGE">Orange Money</option>
              <option value="WAVE">Wave</option>
              <option value="TOGOCEL">T-Money (Togocel)</option>
            </select>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="mt-6">
          <button
            type="button"
            @click="proceedToVerification"
            :disabled="!isValidForm || isSubmitting"
            class="w-full py-4 px-6 rounded-2xl font-bold text-white shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:grayscale text-sm flex items-center justify-center gap-2"
            :class="isValidForm && !isSubmitting ? 'bg-gradient-to-r from-rose-500 to-orange-600 shadow-rose-500/30 hover:shadow-xl' : 'bg-slate-300 shadow-none'"
          >
            <template v-if="!isSubmitting">
              <span>{{ isFedapayPilotProvider ? 'Continuer vers la vérification' : 'Enregistrer le compte' }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </template>
            <template v-else>
              <svg class="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              <span>Vérification...</span>
            </template>
          </button>
        </div>
      </div>
    </DialogComponent>

    <!-- Standalone FedaPay Pilot Verification Modal -->
    <AddFedapayWithdrawalAccount
      ref="fedapayVerification"
      @verified="onFedapayVerified"
      @cancelled="onFedapayCancelled"
      @error="onFedapayError"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';
import AddFedapayWithdrawalAccount from './AddFedapayWithdrawalAccount.vue';
import { getProviderToUse, checkFedapayAccount } from '../../serializer.js';

const modal = ref(null);
const fedapayVerification = ref(null);

const ownerName = ref('');
const phoneNumber = ref('');
const operator = ref('MTN');
const isSubmitting = ref(false);
const isFedapayPilotProvider = ref(true);

const isValidForm = computed(() => {
  return (
    ownerName.value.trim().length >= 2 &&
    phoneNumber.value.replace(/\s+/g, '').length >= 8 &&
    !!operator.value
  );
});

async function showModal() {
  const userStore = getStore('user');
  const userData = userStore?.state?.current || {};
  const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(' ') || userData.name || '';
  ownerName.value = fullName;
  phoneNumber.value = userData.phoneNumber || '';
  operator.value = 'MTN';
  isSubmitting.value = false;

  try {
    const provider = await getProviderToUse(false);
    isFedapayPilotProvider.value = (provider || '').toLowerCase() === 'fedapay-pilot';
  } catch (e) {
    isFedapayPilotProvider.value = false;
  }

  modal.value?.show();
}

function hideModal() {
  modal.value?.hide();
}

function resetForm() {
  ownerName.value = '';
  phoneNumber.value = '';
  operator.value = 'MTN';
  isSubmitting.value = false;
}

async function proceedToVerification() {
  if (!isValidForm.value || isSubmitting.value) return;

  isSubmitting.value = true;

  const cleanPhone = phoneNumber.value.replace(/\s+/g, '').trim();
  const rawPhone = cleanPhone.startsWith('229') && cleanPhone.length > 8 ? cleanPhone.slice(3) : cleanPhone;

  const accountData = {
    owner: ownerName.value.trim(),
    accountLabel: ownerName.value.trim(),
    accountOwnerName: ownerName.value.trim(),
    rawPhoneNumber: rawPhone,
    phoneNumber: cleanPhone,
    operator: operator.value,
    country: 'Bénin',
    countryCode: 'BJ',
  };

  try {
    // 1. Vérifier quel provider est configuré
    const provider = await getProviderToUse(false);
    const isFedapayPilot = (provider || '').toLowerCase() === 'fedapay-pilot';

    if (isFedapayPilot) {
      // 2. Si fedapay-pilot : vérifier si le compte est déjà validé sur FedaPay
      const checkRes = await checkFedapayAccount(rawPhone, operator.value);

      if (checkRes.exists && checkRes.isVerified) {
        // Le compte est déjà vérifié sur FedaPay, on l'ajoute directement dans MonRapidoo
        bus.emit(constants.EVENTS.WALLET.ACCOUNT_CREATE, {
          accountLabel: accountData.owner,
          owner: accountData.owner,
          phoneNumber: accountData.phoneNumber,
          operator: accountData.operator,
          country: accountData.country,
        });
        hideModal();
        isSubmitting.value = false;
        return;
      }

      // Si le compte n'est pas encore vérifié sur FedaPay, lancer la vérification 1 FCFA
      hideModal();
      isSubmitting.value = false;
      if (fedapayVerification.value) {
        fedapayVerification.value.startVerification(accountData);
      }
    } else {
      // Si autre provider (ex: FeexPay) : pas de FedaPay Pilot, enregistrement direct
      bus.emit(constants.EVENTS.WALLET.ACCOUNT_CREATE, {
        accountLabel: accountData.owner,
        owner: accountData.owner,
        phoneNumber: accountData.phoneNumber,
        operator: accountData.operator,
        country: accountData.country,
      });
      hideModal();
      isSubmitting.value = false;
    }
  } catch (error) {
    console.error('Erreur lors de la vérification du provider :', error);
    isSubmitting.value = false;
    // Fallback: enregistrer directement le compte
    bus.emit(constants.EVENTS.WALLET.ACCOUNT_CREATE, {
      accountLabel: accountData.owner,
      owner: accountData.owner,
      phoneNumber: accountData.phoneNumber,
      operator: accountData.operator,
      country: accountData.country,
    });
    hideModal();
  }
}

function onFedapayVerified(verifiedData) {
  // FedaPay verified! Now save into MonRapidoo Accounts API via event bus
  bus.emit(constants.EVENTS.WALLET.ACCOUNT_CREATE, {
    accountLabel: verifiedData.owner || verifiedData.accountLabel,
    owner: verifiedData.owner,
    phoneNumber: verifiedData.phoneNumber || verifiedData.rawPhoneNumber,
    operator: verifiedData.operator,
    country: verifiedData.country || 'BJ',
  });
}

function onFedapayCancelled() {
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    type: 'info',
    title: 'Annulé',
    body: "L'ajout de compte a été interrompu.",
  });
}

function onFedapayError(err) {
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    type: 'error',
    title: 'Erreur de vérification',
    body: err?.message || "Échec de la validation du compte.",
  });
}

defineExpose({
  showModal,
  hideModal
});
</script>
